/**
 * US-504a. One candidate's judging session: the draft, and every surface that
 * renders it.
 *
 * WHY THIS EXISTS. The draft (verdict / chip / note) used to be `useState`
 * inside `CalibrationJudgeCard`, reset by the page remounting the card per
 * candidate with `key={row.id}`. The dossier's judging rail renders the same
 * three values, and two copies of a draft is two answers to "what did the
 * recruiter decide?" — so the state moved up here and the `key` moved with it.
 *
 * That relocation is deliberately a RELOCATION and not a rewrite: a different
 * candidate is still a different component instance, so there is no
 * `useEffect` reset and no window between render and effect in which the
 * previous candidate's note is on screen. Effect-driven resets are how the
 * earlier cursor bug shipped.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CALIBRATION_CHIPS, chipsForVerdict } from '../../constants/calibration-chips';
import type { Verdict } from '../../constants/calibration-chips';
import { useCalibrationIdentity } from '../../hooks/useCalibrationIdentity';
import CalibrationJudgeCard from './CalibrationJudgeCard';
import type { CalibrationCandidate } from './CalibrationJudgeCard';
import CalibrationDossier from './CalibrationDossier';
import type { CalibrationJobSpecState } from './CalibrationDossier';
import { chipForKey, VERDICT_OPTIONS } from './CalibrationJudgingControls';
import type { JudgingDraft, JudgingHandlers, IdentityState } from './calibration-judging';
import { CALIBRATION_RESEARCH_SECONDS_MAX } from '../../types/calibration-api';

export interface JudgingResult {
  verdict: Verdict;
  reasonChip: string | null;
  note: string;
  /**
   * US-503/504e. Seconds spent in the dossier, accumulated across opens.
   *
   * ABSENT — not `0` — when the reading view was never opened. The server keeps
   * NULL distinct from zero on purpose, so "judged on the fast path" and
   * "opened the dossier and closed it instantly" stay different facts; sending
   * a 0 here would collapse them and put a false provenance claim in the
   * column that answers "how often is research actually used?".
   */
  researchSeconds?: number;
}

interface CalibrationJudgingSessionProps {
  agentId: number;
  candidate: CalibrationCandidate;
  autoAdvance: boolean;
  onAutoAdvanceChange: (next: boolean) => void;
  isLast: boolean;
  onJudge: (result: JudgingResult) => void;
  isEditing?: boolean;
  /** Prefill for edit mode. Read once at mount — see the note on the state below. */
  initialVerdict?: Verdict | null;
  initialChip?: string | null;
  initialNote?: string;
  onCancel?: () => void;
  /**
   * US-503. What the SERVER has recorded for this row, or null. The marker
   * renders from this on a row reached through "← Previous candidate", which
   * is why the duration is a column and not a client-side timer: this page
   * polls every ten seconds and re-renders judged rows from server data.
   */
  initialResearchSeconds: number | null;
  /** "Global Product Manager, Hardware · Round 1 · Candidate 4 of 12". */
  contextLine: string;
  /** The capped copy from the polled payload, and its honest flag (US-501). */
  fallbackRequirements: string | null;
  fallbackRequirementsTruncated: boolean;
  fallbackScreeningCriteria: string | null;
  /**
   * US-502. The uncapped job spec. The session decides WHEN to ask for it (on
   * first open, never on the fast path) by calling `onDossierFirstOpen`; the
   * page owns the query so its cache outlives this per-candidate instance.
   */
  jobSpec: CalibrationJobSpecState;
  onDossierFirstOpen: () => void;
}

export default function CalibrationJudgingSession({
  agentId,
  candidate,
  autoAdvance,
  onAutoAdvanceChange,
  isLast,
  onJudge,
  isEditing = false,
  initialVerdict = null,
  initialChip = null,
  initialNote = '',
  onCancel,
  initialResearchSeconds,
  contextLine,
  fallbackRequirements,
  fallbackRequirementsTruncated,
  fallbackScreeningCriteria,
  jobSpec,
  onDossierFirstOpen,
}: CalibrationJudgingSessionProps) {
  // Initial-state-from-props is normally an anti-pattern because the state
  // stops tracking the prop. It is correct HERE because the page mounts this
  // session with `key={row.id}` — a different candidate is a different
  // component instance, never a re-render with new props. That remount is
  // load-bearing for the forward path too: without it, the previous
  // candidate's verdict/note silently carried over and got submitted against
  // the next one.
  const [verdict, setVerdict] = useState<Verdict | null>(initialVerdict);
  const [note, setNote] = useState(initialNote);
  /**
   * A retired chip is legacy-RESOLVABLE, not legacy-WRITABLE: `chipsForVerdict`
   * no longer offers it and the judge route rejects it on new writes. So a
   * prefill of a retired id has no button to press — and carrying it in state
   * anyway would mean "Save change" submitted an invisible value the server
   * then 400s, giving the recruiter an error about a reason they never chose
   * and cannot see. Two real production judgments carry the retired
   * `wrong_seniority` (ids 8 and 24, one per agent).
   */
  const initialChipStillOffered =
    initialChip != null &&
    initialVerdict != null &&
    chipsForVerdict(initialVerdict).some((c) => c.id === initialChip);
  const [selectedChip, setSelectedChip] = useState<string | null>(
    initialChipStillOffered ? initialChip : null,
  );

  /**
   * US-504b. `dossierOpen` toggles the two surfaces; `hasOpenedDossier` is
   * one-way and decides whether the dossier is MOUNTED at all.
   *
   * Two flags rather than one because they answer different questions. Once
   * mounted, the dossier is never unmounted — that is what lets a scroll
   * position and a half-typed note survive the toggle, and it is the property
   * imported from Direction A. But mounting it before the recruiter has ever
   * asked would tax the ~15-second path that nine candidates in twelve take,
   * with a reading layout they never see and a fetch they never need.
   */
  const [dossierOpen, setDossierOpen] = useState(false);
  const [hasOpenedDossier, setHasOpenedDossier] = useState(false);
  const openerRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);
  /**
   * REVIEW FINDING L3. `onDossierFirstOpen` starts the page's job-spec query,
   * which is idempotent — but a callback named "first open" that fires on
   * every open asserts something untrue, and it re-rendered the whole page on
   * each `R`. The flag makes the name accurate.
   */
  const firstOpenFiredRef = useRef(false);

  /**
   * US-504e. Reading time, in refs so that ticking does not re-render the page
   * a recruiter is reading on. `performance.now()` rather than `Date.now()`:
   * a clock adjustment mid-round must not be able to produce a negative
   * research time on a real judgment.
   */
  const settledMsRef = useRef(0);
  const openedAtRef = useRef<number | null>(null);
  const [researchSeconds, setResearchSeconds] = useState<number | null>(initialResearchSeconds);

  /** Time so far, INCLUDING an open dossier — the clock is not stopped to read it. */
  const elapsedMs = useCallback(
    () => settledMsRef.current + (openedAtRef.current === null ? 0 : performance.now() - openedAtRef.current),
    [],
  );

  /**
   * Whole seconds, clamped exactly as the server clamps them, or undefined
   * when the dossier was never opened.
   *
   * The floor of 1 is deliberate: any open at all is at least a second's worth
   * of intent, and rounding a 400ms open down to `0` would send the one value
   * that means something else entirely.
   */
  const researchSecondsForPayload = useCallback((): number | undefined => {
    const ms = elapsedMs();
    if (ms <= 0) return undefined;
    return Math.min(Math.max(1, Math.round(ms / 1000)), CALIBRATION_RESEARCH_SECONDS_MAX);
  }, [elapsedMs]);

  const openDossier = useCallback(() => {
    if (dossierOpen) return;
    if (openedAtRef.current === null) openedAtRef.current = performance.now();
    setHasOpenedDossier(true);
    setDossierOpen(true);
    if (!firstOpenFiredRef.current) {
      firstOpenFiredRef.current = true;
      onDossierFirstOpen();
    }
  }, [dossierOpen, onDossierFirstOpen]);

  /**
   * REVIEW FINDING M2. The body of this used to live INSIDE a `setDossierOpen`
   * updater, which also dispatched a nested `setResearchSeconds`. `main.tsx`
   * wraps the app in `StrictMode`, which double-invokes updaters, and React may
   * run one during the render phase; a nested dispatch from a queue-processing
   * updater is unsupported. It happened to work because the ref mutations were
   * idempotent on the second pass — incidental, not designed. Reading
   * `dossierOpen` from the closure and guarding here does the same job with no
   * side effect inside an updater at all.
   */
  const closeDossier = useCallback(() => {
    if (!dossierOpen) return;
    setDossierOpen(false);
    restoreFocusRef.current = true;
    settledMsRef.current = elapsedMs();
    openedAtRef.current = null;
    const seconds = Math.min(
      Math.max(1, Math.round(settledMsRef.current / 1000)),
      CALIBRATION_RESEARCH_SECONDS_MAX,
    );
    // The server writes GREATEST(existing, incoming), so the marker shows the
    // same number the row will end up holding rather than a smaller one.
    setResearchSeconds((previous) => Math.max(previous ?? 0, seconds));
  }, [dossierOpen, elapsedMs]);

  // Focus goes back to the control that opened the dossier, not to the top of
  // the document — "returns you to the exact card" has to be true for the
  // keyboard too, or Esc silently costs a keyboard user their place.
  useEffect(() => {
    if (dossierOpen || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    openerRef.current?.focus();
  }, [dossierOpen]);

  const { identity, isRevealing, error: identityError, reveal } = useCalibrationIdentity(
    agentId,
    candidate.id,
  );

  /**
   * REVIEW FINDING M1. There are TWO reasons a prefilled chip has no button,
   * and they are not the same claim:
   *
   *  - RETIRED (`chip.retired`) — the vocabulary dropped it. `wrong_seniority`
   *    is the only one, and two production judgments carry it (ids 8 and 24).
   *  - NOT OFFERED FOR THIS ANSWER — polarity. The chip is perfectly live, it
   *    just belongs to the other set. Production judgment 18 is `unsure` +
   *    `strong_reach_out`: a positive chip recorded against an answer that is
   *    offered the negative set. Telling that recruiter "Strong — reach out is
   *    retired" is simply false, and it is the same defect class US-495 fixed
   *    in EXCLUSION_TERM_DROP_EXPLANATION — copy asserting a reason the data
   *    does not support.
   *
   * Derived from the CURRENT verdict and selection, not from `initialVerdict`.
   * The old version keyed off the prefill alone, so switching to Yes and
   * re-picking the very same chip left the notice still on screen insisting it
   * was retired. Once the chip is either offered or actually selected, there
   * is nothing left to explain.
   */
  const initialChipDefinition =
    initialChip != null ? CALIBRATION_CHIPS.find((c) => c.id === initialChip) : undefined;
  const initialChipOfferedNow =
    initialChip != null && verdict != null && chipsForVerdict(verdict).some((c) => c.id === initialChip);
  const unavailableChip =
    initialChip != null && !initialChipOfferedNow && selectedChip !== initialChip
      ? {
          label: initialChipDefinition?.label ?? initialChip,
          retired: initialChipDefinition?.retired === true,
        }
      : null;

  const handleVerdict = useCallback((v: Verdict) => {
    setVerdict(v);
    // The chip sets are verdict-conditional, so a chip selected under the old
    // verdict may not exist under the new one — carrying it over would record a
    // sign-flipped signal (the exact thing the server now 400s on).
    setSelectedChip(null);
  }, []);

  const submit = useCallback(
    (chip: string | null) => {
      if (!verdict) return;
      const seconds = researchSecondsForPayload();
      onJudge(
        seconds === undefined
          ? { verdict, reasonChip: chip, note }
          : { verdict, reasonChip: chip, note, researchSeconds: seconds },
      );
    },
    [verdict, note, onJudge, researchSecondsForPayload],
  );

  const handleChip = useCallback(
    (chipId: string) => {
      if (!verdict) return;
      // US-492. Auto-advance is a FORWARD-path convenience. In edit mode the
      // recruiter is re-reading a judgment they already made; submitting on the
      // first chip they touch would throw away the undo they just used.
      //
      // US-504b adds the second exception: not while the dossier is open. The
      // whole premise of a deliberate reading mode is that the recruiter is
      // taking their time; jumping to the next person the instant they touch a
      // reason is the exact opposite, and it would throw away the reading they
      // were part-way through. In the dossier a chip SELECTS, and the rail's
      // own buttons submit.
      if (autoAdvance && !isEditing && !dossierOpen) {
        // Item 4: the note lives ABOVE the chips now, so it is already typed by
        // the time a chip is clicked — read current state at click time.
        submit(chipId);
        return;
      }
      setSelectedChip((prev) => (prev === chipId ? null : chipId));
    },
    [verdict, autoAdvance, isEditing, dossierOpen, submit],
  );

  const handleSave = useCallback(() => submit(selectedChip), [submit, selectedChip]);

  /**
   * US-504b. The keyboard contract. Twelve candidates is a repetitive task and
   * the mouse is the slow part of it.
   *
   *   1 2 3   verdict            R      open the dossier
   *   Q…U     reason chip        Esc    back to the card
   *   ↵       save & next
   *
   * FIELD GUARD FIRST. `R` is a letter and the note is a textarea: a global
   * handler that does not check its target makes the word "research"
   * impossible to type. Inside a field, Escape blurs and nothing else fires.
   *
   * A single document listener rather than one per surface, because both
   * surfaces are mounted at once and two listeners would fire twice per key.
   *
   * REVIEW FINDING L4. The listener is attached ONCE and reads the current
   * handlers through a ref. It used to depend on `handleSave`, which changes
   * on every keystroke in the note, so the document listener was torn down and
   * re-added per character typed — the fast path was not quite as untouched as
   * the comment above claimed.
   */
  const latest = useRef({ dossierOpen, verdict, openDossier, closeDossier, handleVerdict, handleChip, handleSave });
  latest.current = { dossierOpen, verdict, openDossier, closeDossier, handleVerdict, handleChip, handleSave };

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const { dossierOpen, verdict, openDossier, closeDossier, handleVerdict, handleChip, handleSave } =
        latest.current;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT' || target?.isContentEditable) {
        if (event.key === 'Escape') target?.blur();
        return;
      }

      const key = event.key.toLowerCase();

      if (key === 'escape') {
        if (dossierOpen) {
          event.preventDefault();
          closeDossier();
        }
        return;
      }
      if (key === 'r') {
        if (!dossierOpen) {
          event.preventDefault();
          openDossier();
        }
        return;
      }
      if (key === 'enter') {
        /**
         * REVIEW FINDING C1 (Critical). A focused control OWNS its own Enter:
         * the browser synthesises a click from it, and calling
         * `preventDefault()` here suppresses that click and runs `handleSave()`
         * in its place. The recruiter presses Enter meaning "activate the
         * button I have focused" — Open dossier, Reveal identity, Cancel, Save
         * & go back to the card, ← Previous candidate, Back to judging — and
         * instead a verdict row is written and the queue advances.
         *
         * That is the worst possible failure for this feature: it puts a
         * judgment the recruiter never made into the exact table the whole
         * epic exists to keep honest. Enter is the FAST PATH's save key, for a
         * recruiter whose focus is nowhere in particular; it is not a global
         * submit that outranks the control under the cursor.
         */
        if (
          target?.closest(
            'button, a[href], select, summary, [role="button"], [role="link"], [role="menuitem"]',
          )
        ) {
          return;
        }
        if (verdict) {
          event.preventDefault();
          handleSave();
        }
        return;
      }
      const option = VERDICT_OPTIONS.find((o) => o.key === key);
      if (option) {
        event.preventDefault();
        handleVerdict(option.verdict);
        return;
      }
      if (verdict) {
        const chipId = chipForKey(verdict, key);
        if (chipId) {
          event.preventDefault();
          handleChip(chipId);
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const draft: JudgingDraft = { verdict, selectedChip, note, unavailableChip };
  const handlers: JudgingHandlers = {
    onVerdictChange: handleVerdict,
    onChipClick: handleChip,
    onNoteChange: setNote,
    onSave: handleSave,
  };
  const identityState: IdentityState = {
    identity,
    isRevealing,
    error: identityError,
    onReveal: () => void reveal(),
  };

  return (
    <>
      {/* REVIEW FINDING H1. Toggled with `visibility`, never conditionally
          rendered and — the correction — never with `hidden`/`display:none`
          either.

          "Always mounted" preserves React state and DOM node identity. It does
          NOT preserve scroll offsets: an element with `display:none` has no
          layout box, so every scroll container inside it drops its `scrollTop`
          and returns to 0 on re-display, in every major engine. The stated
          promise of this whole design — "a scroll position two thirds down a
          résumé survives the toggle" — was therefore false for the scroll half
          of it while being true for the note half.

          `visibility: hidden` keeps the layout box (so scroll offsets survive),
          while still removing the surface from the accessibility tree, from tab
          order and from hit-testing — which is what stops the failure mode this
          rule was written for: both surfaces live at once, each with its own
          set of verdict buttons. An inline style rather than a class because it
          must hold identically under jsdom, where no Tailwind stylesheet is
          loaded, and under the Chrome extension's own CSS. */}
      <div
        data-testid="calibration-card"
        style={{ visibility: dossierOpen ? 'hidden' : undefined }}
      >
        <CalibrationJudgeCard
          candidate={candidate}
          draft={draft}
          handlers={handlers}
          identity={identityState}
          autoAdvance={autoAdvance}
          onAutoAdvanceChange={onAutoAdvanceChange}
          isLast={isLast}
          isEditing={isEditing}
          onCancel={onCancel}
          onOpenDossier={openDossier}
          openDossierRef={openerRef}
          researchSeconds={researchSeconds}
        />
      </div>
      {hasOpenedDossier && (
        <CalibrationDossier
          open={dossierOpen}
          candidate={candidate}
          contextLine={contextLine}
          jobSpec={jobSpec}
          fallbackRequirements={fallbackRequirements}
          fallbackRequirementsTruncated={fallbackRequirementsTruncated}
          fallbackScreeningCriteria={fallbackScreeningCriteria}
          draft={draft}
          handlers={handlers}
          identity={identityState}
          isEditing={isEditing}
          isLast={isLast}
          onClose={closeDossier}
          onSaveAndReturn={() => {
            closeDossier();
            handleSave();
          }}
        />
      )}
    </>
  );
}
